<template>
	<DialogControl v-model="dialogOpen" :attach="overlayTarget">
		<template v-slot:activator="{ props: activatorProps }">
			<v-text-field :model-value="displayValue" v-bind="activatorProps" density="compact" variant="outlined" :label="label" readonly></v-text-field>
		</template>

		<template v-slot:default>
			<v-card class="tree-view-control">
				<v-card-title class="headline tree-view-control__title">
					<v-icon>filter_alt</v-icon>
					<span class="tree-view-control__title-text">{{ label }}</span>
					<v-chip v-if="showSelectedCount" class="tree-view-control__count" size="small" color="primary" variant="tonal">{{ selectedCount }}</v-chip>
				</v-card-title>

				<v-alert v-if="showBranchLegend" class="tree-view-control__legend" type="info" variant="tonal" density="compact" closable @click:close="dismissBranchLegend">
					<v-icon size="small">account_tree</v-icon>
					{{ t('SelectBranchLegend') }}
				</v-alert>

				<v-sheet class="pa-2">
					<v-text-field v-model="searchInput" clear-icon="cancel" :label="t('Search')" density="compact" variant="outlined" prepend-inner-icon="search" clearable flat hide-details></v-text-field>
				</v-sheet>

				<template v-if="selectAll">
					<v-list-item class="tree-view-control__select-all" @click="toggleAll">
						<template v-slot:prepend>
							<v-checkbox-btn :model-value="allSelected"></v-checkbox-btn>
							<span class="ml-2">{{ allSelected ? t('DeselectAll') : t('SelectAll') }}</span>
						</template>
					</v-list-item>
					<v-divider></v-divider>
				</template>

				<div v-if="isTreeEmpty" class="tree-view-control__empty">
					<v-icon size="large" color="medium-emphasis">inbox</v-icon>
					<span>{{ t('NoData') }}</span>
				</div>

				<v-treeview v-else v-model="tempSelectItems" v-model:opened="openedNodes" :items="treeItems" :search="search" :custom-filter="treeFilter" :items-registration="itemsRegistration" item-value="id" select-strategy="independent" expand-icon="keyboard_arrow_right" collapse-icon="keyboard_arrow_down" selectable>
					<template v-slot:toggle="{ props: toggleProps, isOpen, isSelected }">
						<v-badge color="success" :model-value="isSelected">
							<template v-slot:badge>
								<v-icon>check</v-icon>
							</template>
							<v-btn v-bind="toggleProps" :color="isSelected ? 'success' : 'medium-emphasis'" :variant="isOpen ? 'outlined' : 'tonal'"></v-btn>
						</v-badge>
					</template>

					<template v-if="branchSelection" v-slot:append="{ item }">
						<v-btn v-if="item.children?.length" class="tree-view-control__branch" :color="isFullBranch(item.id) ? 'primary' : 'medium-emphasis'" variant="text" density="comfortable" size="small" icon :aria-label="t('SelectBranch')" :aria-pressed="isFullBranch(item.id)" @click.stop.prevent="toggleBranch(item.id)">
							<v-icon>account_tree</v-icon>
							<v-tooltip activator="parent" location="top" :attach="overlayTarget || undefined" :text="isFullBranch(item.id) ? t('DeselectBranch') : t('SelectBranch')"></v-tooltip>
						</v-btn>
					</template>

					<template v-slot:no-data>
						<div class="tree-view-control__empty">
							<v-icon size="large" color="medium-emphasis">search_off</v-icon>
							<span>{{ t('NoResultForSearch') }}</span>
						</div>
					</template>
				</v-treeview>

				<v-card-actions>
					<v-btn :text="t('Clear')" variant="text" color="error" @click="clearSelection"></v-btn>
					<v-btn :text="t('Cancel')" variant="text" @click="cancel"></v-btn>
					<v-btn :text="t('Valid')" color="primary" variant="tonal" @click="confirm"></v-btn>
				</v-card-actions>
			</v-card>
		</template>
	</DialogControl>
</template>

<script setup>
// #region Imports

import { ref, shallowRef, inject, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { deviceUtils } from '@/utils/core/deviceUtils';
import { buildTreeIndex, computeFullBranchIds, toggleBranchSelection, collectSearchOpenedIds, nodeMatchesQuery } from '@/utils/ui/treeSelection';
import DialogControl from '@/components/controls/DialogControl.vue';

// #endregion

// #region Refs & Vars

const props = defineProps({
	label: { type: String, default: '' },
	items: { type: Array, default: () => [] },
	titleKey: { type: String, default: 'name' },
	titleKeyTranslate: { type: Boolean, default: false },
	multiple: { type: Boolean, default: false },
	required: { type: Boolean, default: false },
	branchSelection: { type: Boolean, default: false },
	selectAll: { type: Boolean, default: false },
	showSelectedCount: { type: Boolean, default: false },
	expandOnSearch: { type: Boolean, default: false },
	searchFields: { type: Array, default: () => ['title'] },
	itemsRegistration: { type: String, default: 'render' },
});
const emit = defineEmits(['change']);

const t = useTranslator();
const overlayTarget = inject('overlayTarget', null);
const searchInput = shallowRef(null);
const search = shallowRef(null);
const dialogOpen = ref(false);
const openedNodes = shallowRef([]);

const BRANCH_LEGEND_STORAGE_PREFIX = 'treeViewControl.branchLegendSeen';

// #endregion

// #region Filters Data

const selectItems = shallowRef([]);
const tempSelectItems = shallowRef([]);

const treeItems = computed(() => {
	const stripEmptyChildren = (nodes) =>
		nodes.map((node) => {
			if (!node.children || node.children.length === 0) {
				const { children, ...rest } = node;
				return rest;
			}
			return { ...node, children: stripEmptyChildren(node.children) };
		});
	return stripEmptyChildren(props.items);
});

const treeIndex = computed(() => buildTreeIndex(props.items));

const allFlatItems = computed(() => [...treeIndex.value.nodeById.values()]);

const isTreeEmpty = computed(() => treeIndex.value.order.length === 0);

const displayValue = computed(() => {
	if (!selectItems.value.length) return '';
	return `${selectItems.value.length} ${t('Selected')}`;
});

const selectedCount = computed(() => tempSelectItems.value.length);

const filterIsDirty = computed(() => {
	const totalCount = props.selectAll ? treeIndex.value.order.length : props.items.length;
	if (props.multiple) return selectItems.value.length > 0 && selectItems.value.length !== totalCount;
	return selectItems.value.length > 0;
});

const fullBranchIds = computed(() => computeFullBranchIds(treeIndex.value, new Set(tempSelectItems.value)));

function isFullBranch(id) {
	return fullBranchIds.value.has(id);
}

const allSelected = computed(() => treeIndex.value.order.length > 0 && tempSelectItems.value.length === treeIndex.value.order.length);

const normalizedQuery = computed(() =>
	String(search.value ?? '')
		.trim()
		.toLocaleLowerCase()
);

const usesDefaultSearchFields = computed(() => props.searchFields.length === 1 && props.searchFields[0] === 'title');

const treeFilter = computed(() => {
	if (usesDefaultSearchFields.value) return undefined;
	return (value, query, item) => (nodeMatchesQuery(item?.raw ?? item, String(query ?? '').toLocaleLowerCase(), props.searchFields) ? true : -1);
});

const searchOpenedNodes = computed(() => {
	const query = normalizedQuery.value;
	if (!query) return [];
	return collectSearchOpenedIds(treeIndex.value, (node) => nodeMatchesQuery(node, query, props.searchFields));
});

// #endregion

// #region Methods

const normalizeSelection = (items) => (Array.isArray(items) ? [...items] : []);

function toggleBranch(id) {
	tempSelectItems.value = toggleBranchSelection(treeIndex.value, tempSelectItems.value, id, isFullBranch(id));
}

function toggleAll() {
	tempSelectItems.value = allSelected.value ? [] : [...treeIndex.value.order];
}

function clearSelection() {
	tempSelectItems.value = [];
}

function cancel() {
	dialogOpen.value = false;
}

function confirm() {
	selectItems.value = normalizeSelection(tempSelectItems.value);
	dialogOpen.value = false;
	emit('change', selectItems.value);
}

let searchTimer = null;
let openedBeforeSearch = null;

watch(searchInput, (value) => {
	clearTimeout(searchTimer);
	searchTimer = setTimeout(() => {
		search.value = value;
	}, 200);
});

watch([normalizedQuery, searchOpenedNodes], ([query, autoOpened]) => {
	if (!props.expandOnSearch) return;
	if (!query) {
		if (openedBeforeSearch !== null) {
			openedNodes.value = openedBeforeSearch;
			openedBeforeSearch = null;
		}
		return;
	}
	if (openedBeforeSearch === null) openedBeforeSearch = [...openedNodes.value];
	openedNodes.value = [...new Set([...openedBeforeSearch, ...autoOpened])];
});

watch(dialogOpen, (isOpen) => {
	if (!isOpen) return;
	tempSelectItems.value = normalizeSelection(selectItems.value);
	searchInput.value = null;
	search.value = null;
	openedBeforeSearch = null;
});

// #endregion

// #region Branch legend

const isTouchDevice = ref(deviceUtils.isTouchDevice());
const legendDismissed = ref(true);
const showBranchLegend = computed(() => props.branchSelection && isTouchDevice.value && !legendDismissed.value);

function legendStorageKey() {
	return `${BRANCH_LEGEND_STORAGE_PREFIX}.${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}`;
}

function dismissBranchLegend() {
	legendDismissed.value = true;
	try {
		localStorage.setItem(legendStorageKey(), '1');
	} catch {}
}

onMounted(() => {
	try {
		legendDismissed.value = localStorage.getItem(legendStorageKey()) === '1';
	} catch {
		legendDismissed.value = false;
	}
});

onBeforeUnmount(() => {
	clearTimeout(searchTimer);
});

// #endregion

defineExpose({
	getValues() {
		return selectItems.value.length > 0 ? selectItems.value : null;
	},
	getFullValues() {
		const selected = new Set(selectItems.value);
		return allFlatItems.value.filter((item) => selected.has(item.id));
	},
	isDirty() {
		return filterIsDirty.value;
	},
	isValid() {
		if (!props.required) return true;
		if (props.multiple) return Array.isArray(selectItems.value) && selectItems.value.length > 0;
		return selectItems.value.length > 0;
	},
	isRequired() {
		return props.required;
	},
	selectItems(items) {
		const normalizedItems = normalizeSelection(items);
		selectItems.value = normalizedItems;
		tempSelectItems.value = normalizedItems;
		emit('change', selectItems.value);
	},
	reset() {
		selectItems.value = [];
		tempSelectItems.value = [];
		emit('change', selectItems.value);
	},
});
</script>
